import * as XLSX from 'xlsx';

// Formate HH:MM, HH:MM:SS ou ISO datetime en string lisible
const fmtHeure = (h) => {
  if (!h) return '-';
  const s = String(h);
  const idx = s.indexOf('T');
  if (idx !== -1) return s.substring(idx + 1, idx + 6); // ISO: "YYYY-MM-DDTHH:MM:SS"
  return s.substring(0, 5); // "HH:MM" ou "HH:MM:SS"
};

// Formate temps_total (HH:MM) en "Xh YYmin" ou "Zmin"
const fmtTemps = (t) => {
  if (!t) return '-';
  const parts = String(t).split(':');
  if (parts.length < 2) return t;
  const h   = parseInt(parts[0], 10) || 0;
  const m   = parseInt(parts[1], 10) || 0;
  const tot = h * 60 + m;
  if (tot === 0) return '-';
  if (h > 0) return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
  return `${m}min`;
};

const fmt = (v) => (v !== null && v !== undefined && v !== '' ? v : '-');

export const genererRapportQuotidienAmbulancesExcel = ({ date, resume, missions }) => {
  const wb = XLSX.utils.book_new();

  /* ───────────────────────────────────────────────
   * Feuille 1 : Résumé
   * ─────────────────────────────────────────────── */
  const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });

  const resumeData = [
    ['RAPPORT QUOTIDIEN – DEMANDES AMBULANCE'],
    ['OmniDoc Santé · SAMU Regulation'],
    [],
    ['Date du rapport', formattedDate],
    ['Généré le', new Date().toLocaleString('fr-FR')],
    [],
    ['── RÉSUMÉ ──'],
    ['Total missions',        resume.total],
    ['Missions terminées',    resume.terminees],
    ['Missions annulées',     resume.annulees],
    ['Taux de réalisation',   resume.total > 0
      ? `${Math.round((resume.terminees / resume.total) * 100)}%`
      : '-'],
  ];

  const wsResume = XLSX.utils.aoa_to_sheet(resumeData);

  // Largeurs colonnes résumé
  wsResume['!cols'] = [{ wch: 25 }, { wch: 40 }];

  // Styles : titre en gras (sheetjs community = pas de styling riche, on structure bien les données)
  XLSX.utils.book_append_sheet(wb, wsResume, 'Résumé');

  /* ───────────────────────────────────────────────
   * Feuille 2 : Détail des missions
   * ─────────────────────────────────────────────── */
  const headers = [
    'N° Mission',
    'Date',
    'H. Affectation',
    'H. Départ',
    'H. Arrivée',
    'H. Redépart',
    'H. Fin',
    'Durée totale',
    'Ambulance (Immat.)',
    'Type Ambulance',
    'Hôpital départ',
    'Ville départ',
    'Hôpital destination',
    'Ville destination',
    'Ambulancier',
    'Patient',
    'Âge patient',
    'Sexe patient',
    'Urgence',
    'Cause',
    'Adresse départ',
    'Adresse destination',
    'Ville',
    'État',
    'Type mission',
    'Motif annulation',
  ];

  const rows = missions.map(m => [
    m.id,
    m.date_mission ? new Date(m.date_mission).toLocaleDateString('fr-FR') : '-',
    fmtHeure(m.heure_affectation),
    fmtHeure(m.heure_depart),
    fmtHeure(m.heure_arrivee),
    fmtHeure(m.heure_redepart),
    fmtHeure(m.heure_fin),
    fmtTemps(m.temps_total),
    fmt(m.ambulance_immatriculation),
    fmt(m.type_ambulance || m.ambulance_type_vehicule),
    fmt(m.hopital_depart_nom),
    fmt(m.hopital_depart_ville),
    fmt(m.hopital_destination_nom),
    fmt(m.hopital_destination_ville),
    fmt(m.ambulancier_nom),
    fmt(m.patient_nom),
    fmt(m.patient_age),
    fmt(m.patient_sexe),
    m.urgence ? 'Oui' : 'Non',
    fmt(m.cause),
    fmt(m.adresse),
    fmt(m.adresse_destination),
    fmt(m.ville),
    m.statut === 'terminée' ? 'Terminée' : m.statut === 'annulée' ? 'Annulée' : fmt(m.statut),
    fmt(m.etat_mission),
    m.statut === 'annulée' ? (
      m.motif_annulation === 'non_disponibilite_infirmier' ? 'Non disponibilité de l\'infirmier' :
      m.motif_annulation === 'par_medecin_reviseur' ? 'Par médecin réviseur' :
      m.motif_annulation === 'demandeur' ? 'Demandeur' :
      fmt(m.motif_annulation)
    ) : '-',
  ]);

  const wsDetail = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Largeurs colonnes détail
  wsDetail['!cols'] = [
    { wch: 10 }, // N°
    { wch: 12 }, // Date
    { wch: 13 }, // H. Affectation
    { wch: 10 }, // H. Départ
    { wch: 10 }, // H. Arrivée
    { wch: 10 }, // H. Redépart
    { wch: 8  }, // H. Fin
    { wch: 12 }, // Durée
    { wch: 16 }, // Ambulance
    { wch: 14 }, // Type ambulance
    { wch: 24 }, // Hôpital départ
    { wch: 16 }, // Ville départ
    { wch: 24 }, // Hôpital destination
    { wch: 16 }, // Ville destination
    { wch: 20 }, // Ambulancier
    { wch: 20 }, // Patient
    { wch: 10 }, // Âge
    { wch: 10 }, // Sexe
    { wch: 8  }, // Urgence
    { wch: 24 }, // Cause
    { wch: 28 }, // Adresse départ
    { wch: 28 }, // Adresse destination
    { wch: 16 }, // Ville
    { wch: 12 }, // État
    { wch: 16 }, // Type mission
    { wch: 30 }, // Motif annulation
  ];

  // Figer la ligne d'en-têtes
  wsDetail['!freeze'] = { xSplit: 0, ySplit: 1 };

  XLSX.utils.book_append_sheet(wb, wsDetail, 'Missions');

  // Téléchargement
  XLSX.writeFile(wb, `rapport-ambulances-${date}.xlsx`);
};
